import { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './TaskExceptions.css';

const API_URL = process.env.REACT_APP_API_URL;

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12  = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const fmtHours = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const BREAK_SHIFT_ROLES = new Set(['SUPPLY_PARTNER', 'SUPPLYMGR', 'KHELP', 'KCHEF', 'KADM']);

const MISSING_LABELS = {
  not_entered: { label: 'Not Entered',     cls: 'grey'   },
  missing_s:   { label: 'Missing S',       cls: 'orange' },
  missing_l:   { label: 'Missing L',       cls: 'orange' },
  missing_sl:  { label: 'Missing S & L',   cls: 'red'    },
};

export default function TaskExceptions() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const today = localToday();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo,   setDateTo]   = useState(today);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const run = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(
        `${API_URL}/my-tasks/exceptions?date_from=${dateFrom}&date_to=${dateTo}`,
        auth()
      );
      setData(r.data);
    } catch {
      setError('Failed to load exceptions.');
    } finally {
      setLoading(false);
    }
  };

  const exceptions = data?.exceptions ?? [];
  const missing    = data?.missing    ?? [];

  return (
    <div className="te-page">
      <h2 className="te-title">Task Exception Report</h2>

      <div className="te-filters">
        <div className="te-field">
          <label className="te-label">From</label>
          <input type="date" className="te-input" value={dateFrom}
            max={today} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="te-field">
          <label className="te-label">To</label>
          <input type="date" className="te-input" value={dateTo}
            min={dateFrom} max={today} onChange={e => setDateTo(e.target.value)} />
        </div>
        <button className="te-btn" onClick={run} disabled={loading}>
          {loading ? 'Loading…' : 'Run Report'}
        </button>
      </div>

      {error && <p className="te-error">{error}</p>}

      {data !== null && (
        <>
          {/* ── Section 1: Exceptions ── */}
          <div className="te-section">
            <h3 className="te-section-title">Exceptions</h3>
            {exceptions.length === 0 ? (
              <p className="te-empty">No task exceptions for this period.</p>
            ) : (
              <div className="te-table-wrap">
                <table className="te-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Shift</th>
                      <th>Hours Worked</th>
                      <th>Gaps (≥ 60 min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exceptions.map((row, i) => (
                      <tr key={i}>
                        <td className="te-name">{row.emp_name}</td>
                        <td className="te-date">{fmtDate(row.date)}</td>
                        <td className="te-shift">{row.isBreakShift ? 'Break' : 'Normal'}</td>
                        <td>
                          {row.hoursSeverity ? (
                            <span className={`te-badge te-badge--${row.hoursSeverity}`}>
                              {fmtHours(row.workedMinutes)}
                              <span className="te-badge-hint">
                                {BREAK_SHIFT_ROLES.has(row.role)
                                  ? (row.hoursSeverity === 'red' ? ' < 9h' : ' < 10h')
                                  : (row.hoursSeverity === 'red' ? ' < 7h' : ' < 8h')}
                              </span>
                            </span>
                          ) : (
                            <span className="te-ok">{fmtHours(row.workedMinutes)}</span>
                          )}
                        </td>
                        <td>
                          {row.gaps.length === 0 ? (
                            <span className="te-none">—</span>
                          ) : (
                            <div className="te-gaps">
                              {row.gaps.map((g, j) => (
                                <span key={j} className={`te-gap-badge te-gap-badge--${g.severity}`}>
                                  {fmtTime(g.from)} – {fmtTime(g.to)} ({g.minutes} min)
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Section 2: Missing / Not Entered ── */}
          <div className="te-section">
            <h3 className="te-section-title">Not Entered / Incomplete</h3>
            {missing.length === 0 ? (
              <p className="te-empty">All employees have S and L entries for this period.</p>
            ) : (
              <div className="te-table-wrap">
                <table className="te-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missing.map((row, i) => {
                      const { label, cls } = MISSING_LABELS[row.reason] || { label: row.reason, cls: 'grey' };
                      return (
                        <tr key={i}>
                          <td className="te-name">{row.emp_name}</td>
                          <td className="te-date">{fmtDate(row.date)}</td>
                          <td>
                            <span className={`te-status te-status--${cls}`}>{label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
